import { Location } from '@angular/common';
import {
    Injector,
    Type,
} from '@angular/core';
import {
    NavigationEnd,
    Router,
} from '@angular/router';
import {
    ClientErrorCodes,
    ErrorTranslationKeys,
    FOLDER_ID_PARAM,
    FolderType,
    IFolder,
} from '@models';
import { FoldersService } from '@services/folders.service';
import { Logger } from '@services/logger/abstract.logger';
import { StorageService } from '@services/storage.service';
import { filterNullOrUndefined } from '@utils/rxjs.utils';
import { TranslatableError } from '@utils/translatable-error';

import { CurrentUserAPI } from 'main/app-state/current-user/current-user.api';
import { FolderAPI } from 'main/app-state/current-user/folder/folder.api';
import { NullableStateManager } from 'main/app-state/generics/state-manager';
import * as queryString from 'query-string';
import {
    BehaviorSubject,
    iif,
    Observable,
    of,
    pipe,
    Subject,
    Subscription,
    throwError,
} from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    switchMapTo,
    takeUntil,
    tap,
} from 'rxjs/operators';

const FOLDER_KEY = 'last-folder-id';

const HOST_PLACEHOLDER_FOLDER = {
    id: 0,
    displayName: '',
};

const NO_FOLDER_PLACEHOLDER = {
    id: NaN,
    displayName: '',
};
/**
 * The current state is the user's current folder.
 */
export class FolderManager extends NullableStateManager<IFolder> implements FolderAPI {

    public type$: Observable<FolderType | null>;
    public isClassic$: Observable<boolean>;

    protected _state = new BehaviorSubject<IFolder | null>(null);

    private _storage: StorageService;
    private _location: Location;
    private _foldersService: FoldersService;
    private _lazyInitializedRouter?: Router;

    private _folderParamSub?: Subscription;

    private get _router() {
        // The router cannot be injected while the DI instantiates AppState for the App Initializer.
        // Otherwise, AppStateFacade would be injected as undefined inside the initializer function.
        if (!this._lazyInitializedRouter) {
            this._lazyInitializedRouter = this._injector.get<Router>(Router);
        }

        return this._lazyInitializedRouter;
    }

    constructor(
        private _injector: Injector,
        private _user: CurrentUserAPI,
    ) {
        super();

        this._storage = this._injector.get<StorageService>(StorageService);
        this._location = this._injector.get<Location>(Location);
        this._foldersService = this._injector.get<FoldersService>(FoldersService);

        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        this._logStateChanges('Folder', this._injector.get<Logger>(Logger as Type<Logger>));

        this._state
            .pipe(
                filterNullOrUndefined(),
            )
            .subscribe(
                folder => this._storage.set(this._folderKey, folder.id),
            );

        this.type$ = this.value$.pipe(
            map(folder => {
                if (!folder) { return null; }

                return folder.type;
            }),
        );

        this.isClassic$ = this.type$.pipe(
            map(folderType =>
                folderType == null || folderType === FolderType.Classic,
            ),
        );
    }

    public set(folderId: IFolder['id'] | null) {
        return iif(
            () => !!this._user.value,
            this._foldersService.getFolderContext(folderId, 1, 0)
                .pipe(
                    tap(folderContext => {
                        const [folder] = folderContext.folders;

                        if (!folder) {
                            throw new Error('NO_FOLDER');
                        }

                        folder.folderContext = folderContext;
                        this._state.next(folder);
                    }),
                    map(() => true),
                    catchError(() => of(false)),
                ),

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),
        );
    }

    public fetch() {
        this._watchOUQueryParam();

        return new Observable<IFolder>(subscriber => {
            if (!this._user.value) {
                return subscriber.error(new TranslatableError(
                    ErrorTranslationKeys.SessionExpired,
                    ClientErrorCodes.SessionExpired,
                ));
            }

            const destroyed$ = new Subject();

            if (this._user.loggedInAsHost) {
                this._state.next(HOST_PLACEHOLDER_FOLDER as IFolder);

                subscriber.next(HOST_PLACEHOLDER_FOLDER as IFolder);
                return subscriber.complete();
            }

            const folderId = Number(this._queryParamFolderId || this._storageFolderId);

            const endStreamIfSucceeded = () => {
                return pipe(
                    tap(success => {
                        if (success) {
                            subscriber.next(this.value!);
                            subscriber.complete();
                        }
                    }),
                    filter(success => !success),
                );
            };

            const fallback$ = this.set(null)
                .pipe(
                    endStreamIfSucceeded(),
                    tap(() => {
                        this._state.next(NO_FOLDER_PLACEHOLDER as IFolder);
                        subscriber.next(NO_FOLDER_PLACEHOLDER as IFolder);
                        subscriber.complete();
                    }),
                );

            iif(
                () => !!folderId,
                this.set(folderId),
                of(false),
            ).pipe(
                endStreamIfSucceeded(),
                switchMapTo(fallback$),
                takeUntil(destroyed$),
            ).subscribe();

            return () => {
                destroyed$.next(true);
                destroyed$.complete();
            };
        });
    }

    public clean() {
        return new Observable<null>((subscriber) => {
            this._state.next(null);
            subscriber.next(null);
            subscriber.complete();
        });
    }

    public get hasAny(): boolean {
        return !!this.value &&
            !isNaN(this.value.id);
    }

    public get type(): FolderType | null {
        if (!this.value) { return null; }

        return this.value.type;
    }

    private get _folderKey() {
        return this._user.value ?
            `${FOLDER_KEY}-${this._user.value.tenancyId || 'Default'}-${this._user.value.id}` :
            `${FOLDER_KEY}-Default`;
    }

    private get _storageFolderId(): IFolder['id'] {
        return this._storage.get(this._folderKey);
    }

    private get _queryParamFolderId() {
        const queryParams = queryString.parse(
            this._location.path().split('?')[1] || '',
        );
        return Number(queryParams.ou) || null;
    }

    private _watchOUQueryParam() {
        if (this._folderParamSub) {
            return;
        }

        this._folderParamSub = this._router.events
            .pipe(
                filter((ev) => ev instanceof NavigationEnd),
                switchMap(() => this._router.routerState.root.queryParamMap),
                map((queryParamMap) => queryParamMap.get(FOLDER_ID_PARAM)),
                filter((ouId) => ouId !== null),
                map(Number),
                filter((ouId) => ouId !== this.value!.id),
                switchMap(() => this.fetch()),
            )
            .subscribe();
    }
}
